import React from "react";
import { Globe2, ClipboardList, ShieldCheck } from "lucide-react";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

const STEPS = [
  { n: "01", t: "You submit your requirement", d: "Tell us about your facility (or your client's), the chemicals involved and the reporting you need — through our assessment form or a short call." },
  { n: "02", t: "Initial qualification", d: "We review your requirement, confirm scope and identify the information needed to prepare accurate deliverables." },
  { n: "03", t: "Secure document & data collection", d: "You share SDS, inventories and facility details through our secure, access-controlled upload. An NDA is available on request." },
  { n: "04", t: "Technical preparation", d: "Our environmental team performs the technical work — data structuring, SDS review, chemical identification, quantity calculations and documentation." },
  { n: "05", t: "Internal QA / review", d: "A separate review step checks calculations, identifiers and documentation for consistency before anything leaves our team." },
  { n: "06", t: "U.S.-facing client review", d: "We deliver structured, review-ready outputs and walk you through them so your team can confirm and finalize." },
  { n: "07", t: "Final delivery", d: "You receive finished, organized deliverables ready for your review and submission. Recurring support is available for annual cycles." },
];

export default function HowItWorks() {
  useSEO({
    title: "How It Works",
    description:
      "How Wasserlab — EHS Remote delivers U.S. environmental compliance work: intake, qualification, secure data collection, technical preparation, QA review and U.S.-facing delivery. Technical delivery is performed remotely by our India-based team.",
    path: "/how-it-works",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">How It Works</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            A structured, transparent delivery process
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Technical delivery is performed remotely by the Wasserlab team based in India, serving U.S. clients. We keep
            the model transparent — you always know who is doing the work and how it is reviewed.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <ol className="relative border-l-2 border-slate-200 pl-8 space-y-10">
            {STEPS.map((s, i) => (
              <li key={i} className="relative" data-testid={`process-step-${i}`}>
                <span className="absolute -left-[41px] flex h-8 w-8 items-center justify-center rounded-full bg-navy-900 text-[11px] font-bold text-amber-500">
                  {s.n}
                </span>
                <h2 className="font-heading text-xl font-bold text-navy-900">{s.t}</h2>
                <p className="mt-1.5 text-base leading-relaxed text-slate-600">{s.d}</p>
              </li>
            ))}
          </ol>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Globe2, t: "Remote delivery", d: "India-based technical team serving U.S. clients." },
              { icon: ClipboardList, t: "Structured workflows", d: "Repeatable, documented preparation steps." },
              { icon: ShieldCheck, t: "Reviewed before delivery", d: "Multi-level QA prior to client handoff." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-6">
                  <Icon className="h-6 w-6 text-emerald-600" aria-hidden="true" />
                  <h3 className="mt-3 font-heading text-base font-bold text-navy-900">{c.t}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <CTASection location="how-it-works-footer" />
    </>
  );
}
